import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Clock, Loader2, Phone, ShieldCheck, Wrench } from "lucide-react";

import { submitLead } from "@/lib/leads.functions";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { CTABlock } from "@/components/CTABlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { company } from "@/data/site";
import solarRepairHero from "@/assets/service-heroes/solar-panel-replacement.jpg.asset.json";

const URL = "https://mckay.steadyautogrowthdemo.com/book/solar-repair";

export const Route = createFileRoute("/book/solar-repair")({
  head: () => ({
    meta: [
      { title: "Book a Solar Repair Visit | McKay Roofing & Solar" },
      { name: "description", content: "Book a solar repair visit in Southern California. Inverter not working? We replace it with a manufacturer warranty and become your ongoing service provider." },
      { name: "keywords", content: "solar repair Southern California, solar inverter replacement, book solar repair, solar panel repair Southern California" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:title", content: "Book a Solar Repair Visit – McKay Roofing & Solar" },
      { property: "og:description", content: "Pick a day and time window for a solar repair visit anywhere in Southern California." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Solar Repair",
          serviceType: "Solar panel and inverter repair",
          provider: {
            "@type": "RoofingContractor",
            name: "McKay Roofing & Solar",
            telephone: company.phone,
            email: company.email,
            address: {
              "@type": "PostalAddress",
              streetAddress: "9520 Padgett St, Suite 107",
              addressLocality: "San Diego",
              addressRegion: "CA",
              postalCode: "92126",
              addressCountry: "US",
            },
          },
          areaServed: "Southern California",
          url: URL,
        }),
      },
    ],
  }),
  component: BookSolarRepairPage,
});

const TIME_WINDOWS = ["7:00 AM – 10:00 AM", "10:00 AM – 1:00 PM", "1:00 PM – 4:00 PM", "4:00 PM – 7:00 PM"];

const ISSUES = [
  "Inverter is off, red light or error code",
  "System stopped producing / monitoring offline",
  "Panels producing less than they used to",
  "Leak or roof damage around the array",
  "Wiring, conduit or rodent damage",
  "Original installer is out of business",
];

function buildDays(count: number) {
  const out: { iso: string; weekday: string; day: string; month: string; closed: boolean }[] = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  let cursor = 1;
  while (out.length < count) {
    const d = new Date(start);
    d.setDate(start.getDate() + cursor);
    cursor += 1;
    const closed = d.getDay() === 0; // closed Sundays
    if (closed) continue;
    out.push({
      iso: d.toISOString().slice(0, 10),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      day: d.toLocaleDateString("en-US", { day: "numeric" }),
      month: d.toLocaleDateString("en-US", { month: "short" }),
      closed,
    });
  }
  return out;
}

function BookSolarRepairPage() {
  const submit = useServerFn(submitLead);
  const navigate = useNavigate();
  const days = useMemo(() => buildDays(14), []);
  const [selectedDay, setSelectedDay] = useState(days[0]?.iso ?? "");
  const [selectedWindow, setSelectedWindow] = useState(TIME_WINDOWS[0]);
  const [issues, setIssues] = useState<string[]>([]);
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function toggleIssue(issue: string) {
    setIssues((prev) => (prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (!smsConsent) {
      toast.error("Please agree to the SMS consent to continue.");
      return;
    }
    if (!selectedDay) {
      toast.error("Please pick a day for your visit.");
      return;
    }

    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") || "");
    const email = String(fd.get("email") || "");
    if (!name || !email) {
      toast.error("Please fill in your name and email.");
      return;
    }

    const notes = String(fd.get("message") || "");
    const payload = {
      name,
      email,
      phone: String(fd.get("phone") || ""),
      address: String(fd.get("address") || ""),
      service: "Solar Repair Booking",
      message: [
        `Requested day: ${selectedDay}`,
        `Requested window: ${selectedWindow}`,
        issues.length ? `Reported issues: ${issues.join("; ")}` : "",
        notes ? `Notes: ${notes}` : "",
      ]
        .filter(Boolean)
        .join("\n"),
      source: "solar-repair-booking",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
    };

    setSubmitting(true);
    try {
      const res = await submit({ data: payload });
      if (res?.ok) {
        form.reset();
        setIssues([]);
        setSmsConsent(false);
        navigate({ to: "/thank-you" });
      } else {
        toast.error(res?.error || "Something went wrong. Please call us instead.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden text-primary-foreground">
          <HeroBackground src={solarRepairHero.url} alt="Technician servicing a solar array on a Southern California roof" overlay="black-soft" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-5xl flex-col justify-center px-4 py-24 sm:px-6 lg:px-8">
            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-accent-foreground">
              <Wrench className="h-3.5 w-3.5" /> Solar repair
            </div>
            <h1 className="mt-5 text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">
              Book a solar repair visit
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              Inverter not working? We will replace it with a manufacturer warranty and become your ongoing service
              provider. Pick a day and a time window below and we will confirm it with you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Value bar */}
        <section className="border-b border-border bg-muted/40">
          <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:grid-cols-3 sm:px-6 lg:px-8">
            {[
              { icon: ShieldCheck, title: "Manufacturer warranty inverters", body: "Replacements registered under the manufacturer's warranty, not a no-name swap." },
              { icon: Wrench, title: "We take over orphaned systems", body: "If the company that installed your solar is gone, we become your service provider." },
              { icon: Clock, title: "Three-hour arrival windows", body: "You get a window, not a whole day of waiting around for a truck." },
            ].map((v) => (
              <div key={v.title} className="flex gap-3">
                <v.icon className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                <div>
                  <div className="font-bold text-primary">{v.title}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Booking form */}
        <section className="bg-background">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Schedule</div>
              <h2 className="mt-3 font-serif text-3xl font-bold text-primary sm:text-4xl">Pick your visit</h2>
              <p className="mt-3 text-muted-foreground">
                We service solar across Southern California, Monday through Saturday. Choose the day that works and we
                will confirm the exact arrival time.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-10 space-y-8 rounded-3xl border border-border bg-card p-6 shadow-elegant sm:p-8">
              {/* Day picker */}
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-primary">
                  <CalendarDays className="h-4 w-4 text-secondary" /> Choose a day
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-7">
                  {days.map((d) => {
                    const active = d.iso === selectedDay;
                    return (
                      <button
                        key={d.iso}
                        type="button"
                        onClick={() => setSelectedDay(d.iso)}
                        aria-pressed={active}
                        className={`rounded-2xl border px-2 py-3 text-center transition-all ${
                          active
                            ? "border-primary bg-primary text-primary-foreground shadow-lg"
                            : "border-border bg-background hover:border-primary"
                        }`}
                      >
                        <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{d.weekday}</div>
                        <div className="text-xl font-extrabold leading-tight">{d.day}</div>
                        <div className="text-[11px] uppercase opacity-80">{d.month}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Window picker */}
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-primary">
                  <Clock className="h-4 w-4 text-secondary" /> Choose an arrival window
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {TIME_WINDOWS.map((w) => {
                    const active = w === selectedWindow;
                    return (
                      <button
                        key={w}
                        type="button"
                        onClick={() => setSelectedWindow(w)}
                        aria-pressed={active}
                        className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-all ${
                          active
                            ? "border-primary bg-primary text-primary-foreground shadow-lg"
                            : "border-border bg-background text-foreground hover:border-primary"
                        }`}
                      >
                        {w}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Issues */}
              <div>
                <div className="text-sm font-bold uppercase tracking-[0.12em] text-primary">What is going on?</div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {ISSUES.map((issue) => {
                    const active = issues.includes(issue);
                    return (
                      <button
                        key={issue}
                        type="button"
                        onClick={() => toggleIssue(issue)}
                        aria-pressed={active}
                        className={`flex items-start gap-2 rounded-2xl border px-4 py-3 text-left text-sm transition-all ${
                          active ? "border-secondary bg-secondary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-primary"
                        }`}
                      >
                        <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-secondary" : "opacity-30"}`} />
                        <span>{issue}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contact details */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" name="name" required className="mt-2" placeholder="Jane Mitchell" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" name="phone" type="tel" required className="mt-2" placeholder="(858) 555-0142" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" required className="mt-2" placeholder="you@email.com" />
                </div>
                <div>
                  <Label htmlFor="address">Property address</Label>
                  <Input id="address" name="address" className="mt-2" placeholder="Street, city" />
                </div>
              </div>

              <div>
                <Label htmlFor="message">Anything else we should know?</Label>
                <Textarea id="message" name="message" rows={4} className="mt-2" placeholder="Inverter brand, error code, when it stopped producing, whether the roof has leaked." />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="sms" checked={smsConsent} onCheckedChange={(v) => setSmsConsent(v === true)} />
                <Label htmlFor="sms" className="text-sm font-normal leading-relaxed text-muted-foreground">
                  I agree to receive calls and text messages from McKay Roofing &amp; Solar about my solar repair
                  request. Message and data rates may apply. Reply STOP to opt out.
                </Label>
              </div>

              <div>
                <Button type="submit" size="lg" disabled={submitting} className="btn-glow w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending request</>) : "Request this visit"}
                </Button>
                <div className="mt-3 text-center text-xs text-muted-foreground">
                  <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
                  <span className="mx-2">|</span>
                  <Link to="/terms" className="hover:text-primary">Terms &amp; Conditions</Link>
                </div>
              </div>
            </form>
          </div>
        </section>

        <CTABlock
          title="Not sure whether it is the inverter or the panels?"
          subtitle="Call us and describe what the system is doing. We can usually narrow it down before a truck rolls."
        />
      </main>
      <SiteFooter />
    </div>
  );
}
